/** Zoho sync tasks: form <-> API, schedule wording, run log labels (pure). */

const pad = (n) => String(n).padStart(2, "0");
const isWhole = (v) => /^\d+$/.test(v ?? "");

/** "02:00 AM" from hours and minutes. */
const clock = (h, m) => {
  const hour = Number(h);
  return `${pad(hour % 12 || 12)}:${pad(m)} ${hour < 12 ? "AM" : "PM"}`;
};

/** { kind: "minutes"|"daily"|"weekly", … } for the crons this page writes; else null. */
const parseCron = (cron) => {
  const p = String(cron || "")
    .trim()
    .split(/\s+/);
  if (p.length !== 5) return null;
  const [min, hour, dom, mon, dow] = p;
  if (min.startsWith("*/") && hour === "*" && dom === "*" && mon === "*")
    return { kind: "minutes", every: min.slice(2) };
  if (!isWhole(min) || !isWhole(hour) || dom !== "*" || mon !== "*")
    return null;
  if (dow === "*") return { kind: "daily", hour, min };
  if (dow === "0") return { kind: "weekly", hour, min };
  return null;
};

/** Short label for the table: "Daily at 02:00 AM". */
export const formatCronLabel = (cron) => {
  if (!cron) return "Not scheduled";
  const c = parseCron(cron);
  if (!c) return cron;
  if (c.kind === "minutes") return `Every ${c.every} mins`;
  const at = clock(c.hour, c.min);
  return c.kind === "weekly" ? `Sundays at ${at}` : `Daily at ${at}`;
};

/** "Runs daily at 12:00 AM Manila time" */
export const cronSentence = (cron) => {
  if (!cron) return "Not scheduled";
  const c = parseCron(cron);
  if (!c) return "Runs on a custom schedule";
  if (c.kind === "minutes") return `Runs every ${c.every} minutes`;
  const at = clock(c.hour, c.min);
  return c.kind === "weekly"
    ? `Runs every Sunday at ${at} Manila time`
    : `Runs daily at ${at} Manila time`;
};

export const cronFromTime = (time, frequency) => {
  const [h, m] = String(time || "00:00").split(":");
  return `${parseInt(m, 10)} ${parseInt(h, 10)} * * ${frequency === "weekly" ? "0" : "*"}`;
};

/** The form's time and frequency; null when the cron can't be shown that way. */
export const timeFromCron = (cron) => {
  const c = parseCron(cron);
  if (!c || c.kind === "minutes") return null;
  return {
    time: `${pad(c.hour)}:${pad(c.min)}`,
    frequency: c.kind === "weekly" ? "weekly" : "daily",
  };
};

export const emptySyncForm = (companyId = "") => ({
  name: "",
  companyId,
  cronExpression: "0 2 * * *",
  isActive: true,
  targetIdentifier: "",
  clientId: "",
  clientSecret: "",
  refreshToken: "",
  dc: "com",
});

/** A saved task in the form. Secrets are never returned, so they start blank. */
export const syncFormFromConfig = (config) => ({
  ...emptySyncForm(config.companyId || ""),
  name: config.name || "",
  cronExpression: config.cronExpression || "0 2 * * *",
  isActive: config.isActive !== false,
  targetIdentifier: config.source?.targetIdentifier || "",
  clientId: config.source?.credentials?.clientId || "",
  dc: config.source?.credentials?.dc || "com",
});

/**
 * POST and PUT share these required fields. A blank secret is left out, which
 * keeps the saved one on PUT. No id, dates, process or has* flags are sent.
 */
export const buildSyncPayload = (form) => {
  const credentials = {
    clientId: form.clientId.trim(),
    dc: form.dc,
  };
  if (form.clientSecret.trim())
    credentials.clientSecret = form.clientSecret.trim();
  if (form.refreshToken.trim())
    credentials.refreshToken = form.refreshToken.trim();
  return {
    name: form.name.trim(),
    companyId: form.companyId,
    sourceType: "ZOHO",
    configType: "ZOHO_TEMPLATE",
    cronExpression: form.cronExpression,
    isActive: form.isActive,
    source: {
      targetIdentifier: form.targetIdentifier.trim(),
      credentials,
    },
  };
};

/* ---------- Run log ---------- */
export const RUN_STATUS = {
  COMPLETED: { label: "Completed", tone: "green" },
  FAILED: { label: "Failed", tone: "red" },
  IN_PROGRESS: { label: "Running", tone: "blue" },
};

export const FILE_STATUS = {
  SUCCESS: { label: "Success", tone: "green" },
  FAILED: { label: "Failed", tone: "red" },
  "PARTIAL SUCCESS": { label: "Not moved", tone: "amber" },
};

export const FILE_FILTERS = [
  { key: "all", label: "All", params: {} },
  { key: "failed", label: "Failed", params: { status: "FAILED" } },
  {
    key: "notMoved",
    label: "Not moved",
    params: { status: "PARTIAL SUCCESS" },
  },
  {
    key: "unauthorized",
    label: "Unauthorized",
    params: { issueType: "UNAUTHORIZED" },
  },
];

const ISSUE_LABEL = {
  UNAUTHORIZED: "Zoho refused access",
  MOVE_FAILED: "Couldn't move",
  EMPLOYEE_NOT_FOUND: "No matching employee",
  SEVERAL_EMPLOYEES: "Several employees match",
  BAD_FILENAME: "Filename problem",
  SYNC_STOPPED: "Sync stopped",
  OTHER: "Other",
};
export const issueLabel = (type) => ISSUE_LABEL[type] || type || "";

export const shortId = (id) => String(id || "").slice(0, 8);

/** "1h 2m", "3m 5s", "12s"; "" while running or when a time is unusable. */
export const runDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return "";
  const ms = new Date(finishedAt) - new Date(startedAt);
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

/** "Schedule", or "Run Now by a@b.ph" */
export const triggerText = ({ trigger, startedBy }) =>
  trigger === "SCHEDULE"
    ? "Schedule"
    : `Run Now${startedBy ? ` by ${startedBy}` : ""}`;

export const movedText = (moved) =>
  moved === true ? "Yes" : moved === false ? "No" : "-";

/** A row with rowNumber 0 and no file name: a whole folder's sync stopped. */
export const isStoppedRow = (row) => row.rowNumber === 0 && !row.fileName;

/** What needs attention on a run; used by the task list. */
export const runProblems = (run) =>
  [
    run.failed > 0 && {
      tone: "red",
      text: `${run.failed} failed`,
    },
    run.notMoved > 0 && {
      tone: "amber",
      text: `${run.notMoved} not moved`,
    },
    run.unauthorized > 0 && {
      tone: "red",
      text: `Zoho refused ${run.unauthorized}`,
    },
  ].filter(Boolean);

/** A cell can't start a spreadsheet formula (file names come from outside). */
const safeCell = (v) => {
  const s = String(v ?? "");
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
};

export const FILE_CSV_HEADER = [
  "File name",
  "Employee",
  "Employee number",
  "Employee branch",
  "Source branches",
  "Document",
  "Result",
  "Moved",
  "Status",
  "Issue type",
  "Issue",
];

export const fileCsvRow = (r) =>
  [
    r.fileName,
    r.employee,
    r.employeeNumber,
    r.employeeBranch,
    (r.folderBranches || []).join("; "),
    r.document,
    r.result,
    movedText(r.moved),
    FILE_STATUS[r.status]?.label || r.status,
    issueLabel(r.issueType),
    r.issue,
  ].map(safeCell);

/* ---------- Import History ---------- */
export const SYNC_MODULE = "GovernmentDocuments";

export const importModuleLabel = (moduleName) =>
  moduleName === SYNC_MODULE ? "Zoho sync" : moduleName;

/** A row of a Zoho sync log (workbook imports have no rawData.fileName). */
export const isSyncRow = (row) => !!row?.rawData?.fileName;

/** Sync log rows include "whole sync stopped" ones with no file. */
export const isSyncBatch = (moduleName, rows = []) =>
  moduleName === SYNC_MODULE || rows.some(isSyncRow);
